import { useEffect, useState } from 'react';

interface RegisterPageProps {
  onBackToLogin: () => void;
}

export function RegisterPage({ onBackToLogin }: RegisterPageProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    role: 'VIEWER'
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // 이메일 인증 관련 상태
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [timer, setTimer] = useState(180); // 3분 = 180초
  const [isTimerActive, setIsTimerActive] = useState(false);

  // 타이머 효과
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // 인증번호 발송
  const handleSendVerificationCode = async () => {
    if (!formData.email.trim()) {
      setError('이메일을 입력해주세요');
      return;
    }
    if (!formData.email.endsWith('@wizbase.co.kr')) {
      setError('@wizbase.co.kr 이메일만 사용 가능합니다');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/auth/send-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '인증번호 발송에 실패했습니다');
      }

      setIsCodeSent(true);
      setTimer(180); // 3분 리셋
      setIsTimerActive(true);
      // 인증번호 발송 성공 시 별도 메시지는 표시하지 않음 (UI에서 자동으로 인증번호 입력창 표시)
    } catch (err: any) {
      setError(err.message || '인증번호 발송에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 인증번호 검증
  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('인증번호를 입력해주세요');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          code: verificationCode
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.valid) {
        throw new Error(data.error || '인증번호가 일치하지 않습니다');
      }

      setIsVerified(true);
      setIsTimerActive(false);
      // success 상태는 회원가입 최종 완료 시에만 사용
    } catch (err: any) {
      setError(err.message || '인증번호 검증에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name.trim()) {
        setError('이름을 입력해주세요');
        return;
      }
    } else if (step === 2) {
      if (!formData.email.trim()) {
        setError('이메일을 입력해주세요');
        return;
      }
      if (!formData.email.endsWith('@wizbase.co.kr')) {
        setError('@wizbase.co.kr 이메일만 사용 가능합니다');
        return;
      }
      if (!isVerified) {
        setError('이메일 인증을 완료해주세요');
        return;
      }
    } else if (step === 3) {
      if (!formData.username.trim()) {
        setError('아이디를 입력해주세요');
        return;
      }
      if (formData.username.length < 3) {
        setError('아이디는 3자 이상이어야 합니다');
        return;
      }
    } else if (step === 4) {
      if (!formData.password) {
        setError('비밀번호를 입력해주세요');
        return;
      }
      if (formData.password.length < 6) {
        setError('비밀번호는 6자 이상이어야 합니다');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('비밀번호가 일치하지 않습니다');
        return;
      }
    }

    if (step < 5) {
      setStep(step + 1);
      setError('');
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          name: formData.name,
          email: formData.email,
          role: formData.role
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '회원가입에 실패했습니다');
      }

      setSuccess('회원가입 완료! OCI 사업부 메일을 확인해주세요. 관리자 승인 후 로그인할 수 있습니다');
    } catch (err: any) {
      setError(err.message || '회원가입에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && step < 5) {
      handleNext();
    } else if (e.key === 'Enter' && step === 5) {
      handleSubmit();
    }
  };

  const roleLabels: { [key: string]: string } = {
    'VIEWER': '조회자',
    'CUSTOMER': '고객사',
    'ENGINEER': '엔지니어',
    'ADMIN': '관리자'
  };

  // 타이머 포맷 (MM:SS)
  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;600;700;800&display=swap');

        * {
          font-family: 'Noto Sans KR', sans-serif;
        }fast_bg.gif

        .register-card {
          position: relative;
          z-index: 2;
          background: rgba(17, 24, 39, 0.92);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          padding: 3.5rem 3rem;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .progress-bar {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 3rem;
        }

        .progress-dot {
          flex: 1;
          height: 4px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .progress-dot.active {
          background: #B73F3F;
        }

        .step-content {
          min-height: 320px;
          animation: fadeIn 0.4s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .step-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }

        .step-subtitle {
          font-size: 1rem;
          color: #d1d5db;
          margin-bottom: 2.5rem;
          font-weight: 400;
        }

        .input-wrapper {
          margin-bottom: 1.5rem;
        }

        .input-label {
          display: block;
          font-size: 0.9375rem;
          font-weight: 600;
          color: #e5e7eb;
          margin-bottom: 0.75rem;
        }

        .input-field {
          width: 100%;
          padding: 1.125rem 1.25rem;
          background: rgba(31, 41, 55, 0.5);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          color: #ffffff;
          font-size: 1.0625rem;
          font-weight: 500;
          transition: all 0.2s ease;
          outline: none;
        }

        .input-field:hover {
          border-color: rgba(255, 255, 255, 0.2);
          background: rgba(31, 41, 55, 0.7);
        }

        .input-field:focus {
          border-color: #B73F3F;
          background: rgba(31, 41, 55, 0.8);
          box-shadow: 0 0 0 4px rgba(183, 63, 63, 0.2);
        }

        .input-field::placeholder {
          color: #9ca3af;
          font-weight: 400;
        }

        .input-hint {
          font-size: 0.875rem;
          color: #9ca3af;
          margin-top: 0.5rem;
        }

        .role-grid {
          display: block;
          margin-top: 1.5rem;
        }

        .role-card {
          padding: 1.5rem;
          background: rgba(31, 41, 55, 0.5);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
          width: 100%;
        }

        .role-card:hover {
          border-color: #B73F3F;
          background: rgba(31, 41, 55, 0.7);
        }

        .role-card.selected {
          border-color: #B73F3F;
          background: rgba(183, 63, 63, 0.2);
        }

        .role-name {
          font-size: 1.0625rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.25rem;
        }

        .role-desc {
          font-size: 0.8125rem;
          color: #d1d5db;
        }

        .error-message {
          background: rgba(220, 38, 38, 0.15);
          border: 1px solid rgba(252, 165, 165, 0.4);
          color: #fca5a5;
          padding: 1rem 1.25rem;
          border-radius: 14px;
          font-size: 0.9375rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          animation: shake 0.3s ease;
        }

        .success-message {
          background: rgba(6, 182, 212, 0.1);
          border: 2px solid #06b6d4;
          color: #22d3ee;
          padding: 1.25rem 1.5rem;
          border-radius: 16px;
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          box-shadow: 0 0 20px rgba(6, 182, 212, 0.3), inset 0 0 10px rgba(34, 211, 238, 0.1);
          position: relative;
          overflow: hidden;
        }

        .success-message::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(45deg, transparent, rgba(34, 211, 238, 0.1), transparent);
          animation: laser-sweep 3s linear infinite;
        }

        @keyframes laser-sweep {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .button-group {
          display: flex;
          gap: 0.75rem;
          margin-top: 2rem;
        }

        .btn {
          flex: 1;
          padding: 1.125rem;
          border: none;
          border-radius: 24px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-primary {
          background: #B73F3F;
          color: white;
        }

        .btn-primary:hover {
          background: #a33535;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(183, 63, 63, 0.3);
        }

        .btn-primary:active {
          transform: translateY(0);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-secondary {
          background: rgba(255, 255, 255, 0.1);
          color: #d1d5db;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #ffffff;
        }

        .back-link {
          text-align: center;
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .back-link button {
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.9375rem;
        }

        .back-link button:hover {
          color: #B73F3F;
          text-decoration: underline;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          padding: 1rem;
          background: rgba(31, 41, 55, 0.5);
          border-radius: 12px;
          margin-bottom: 0.75rem;
        }

        .summary-label {
          font-size: 0.9375rem;
          color: #9ca3af;
        }

        .summary-value {
          font-size: 0.9375rem;
          font-weight: 600;
          color: #ffffff;
        }
      `}</style>

      <div className="register-card">
        <div className="progress-bar">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`progress-dot ${i <= step ? 'active' : ''}`} />
          ))}
        </div>

        <div className="step-content">
          {step === 1 && (
            <>
              <h2 className="step-title">반가워요!<br />이름을 알려주세요</h2>
              <div className="input-wrapper mt-10">
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="input-field"
                  placeholder="ex) 정위즈"
                  autoFocus
                  maxLength={100}
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="step-title">이메일 인증을<br />진행해주세요</h2>
              <p className="step-subtitle">위즈베이스 임직원만 회원가입이 가능합니다</p>

              <div className="input-wrapper">
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="ex) xxx@wizbase.co.kr"
                  autoFocus
                  disabled={isVerified}
                  style={{
                    background: isVerified ? 'rgba(34, 197, 94, 0.1)' : undefined,
                    borderColor: isVerified ? '#22c55e' : undefined
                  }}
                />
                {!isCodeSent && !isVerified && (
                  <button
                    className="btn btn-primary"
                    onClick={handleSendVerificationCode}
                    disabled={loading}
                    style={{ marginTop: '1rem', width: '100%' }}
                  >
                    {loading ? '발송 중...' : '인증번호 받기'}
                  </button>
                )}
              </div>

              {isCodeSent && !isVerified && (
                <div className="input-wrapper" style={{ marginTop: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <label className="input-label" style={{ margin: 0 }}>인증번호</label>
                    {isTimerActive && (
                      <span style={{
                        color: timer < 60 ? '#ef4444' : '#B73F3F',
                        fontWeight: 700,
                        fontSize: '0.9375rem'
                      }}>
                        {formatTimer(timer)}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => {
                        setVerificationCode(e.target.value);
                        setError('');
                      }}
                      className="input-field"
                      placeholder="6자리 숫자"
                      maxLength={6}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleVerifyCode}
                      disabled={loading || timer === 0}
                      style={{ minWidth: '100px' }}
                    >
                      {loading ? '확인 중...' : '확인'}
                    </button>
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={handleSendVerificationCode}
                    disabled={loading}
                    style={{ marginTop: '0.75rem', width: '100%', fontSize: '0.875rem' }}
                  >
                    인증번호 재발송
                  </button>
                </div>
              )}

              {isVerified && (
                <div style={{
                  background: 'rgba(34, 197, 94, 0.15)',
                  border: '2px solid #22c55e',
                  padding: '1rem 1.25rem',
                  borderRadius: '14px',
                  marginTop: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✅</div>
                  <div style={{ color: '#22c55e', fontWeight: 600 }}>
                    이메일 인증 완료
                  </div>
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="step-title">사용할 아이디를<br />입력해주세요</h2>
              <p className="step-subtitle">3자 이상 입력해주세요</p>
              <div className="input-wrapper">
                <input
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="input-field"
                  placeholder="아이디 입력"
                  autoFocus
                  minLength={3}
                  maxLength={50}
                />
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="step-title">비밀번호를<br />설정해주세요</h2>
              <p className="step-subtitle">6자 이상 입력해주세요</p>
              <div className="input-wrapper">
                <label className="input-label">비밀번호</label>
                <input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="input-field"
                  placeholder="비밀번호"
                  autoFocus
                  minLength={6}
                />
              </div>
              <div className="input-wrapper">
                <label className="input-label">비밀번호 확인</label>
                <input
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="input-field"
                  placeholder="비밀번호 확인"
                />
              </div>
            </>
          )}

          {step === 5 && (
            <>
              <h2 className="step-title">거의 다 왔어요!<br />정보를 확인해주세요</h2>
              <p className="step-subtitle">가입 후 관리자 승인을 기다려주세요</p>

              <div style={{ marginTop: '2rem' }}>
                <div className="summary-item">
                  <span className="summary-label">이름</span>
                  <span className="summary-value">{formData.name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">이메일</span>
                  <span className="summary-value">{formData.email}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">아이디</span>
                  <span className="summary-value">{formData.username}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">역할</span>
                  <span className="summary-value">{roleLabels[formData.role]}</span>
                </div>
              </div>
            </>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="button-group">
            {success ? (
              <button className="btn btn-primary" onClick={onBackToLogin}>
                로그인 화면으로
              </button>
            ) : (
              <>
                {step > 1 && (
                  <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>
                    이전
                  </button>
                )}
                {step < 5 ? (
                  <button className="btn btn-primary" onClick={handleNext}>
                    다음
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? '가입 중...' : '가입 완료'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="back-link">
          <button onClick={onBackToLogin}>이미 계정이 있으신가요? 로그인</button>
        </div>
      </div>
    </>
  );
}
